/*
 * Contact form behaviour: a loading state on submit, and the success message
 * promoted from an inline block to a modal confirmation.
 *
 * The form posts natively and Shopify redirects back to the page, so the
 * loading state only has to survive until that navigation happens, and the
 * confirmation is rendered server-side by Liquid rather than fetched.
 */
class ContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.spinner = this.querySelector('.loading__spinner');
    this.dialog = this.querySelector('.contact-success');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    if (this.dialog) {
      this.openConfirmation();
    }
  }

  onSubmit(event) {
    // The browser is already navigating away, so this only guards a second
    // click landing before the page unloads.
    if (this.submitting) {
      event.preventDefault();
      return;
    }
    if (!this.form.checkValidity()) return;

    this.submitting = true;
    this.submitButton.classList.add('loading');
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');
    this.spinner?.classList.remove('hidden');
  }

  openConfirmation() {
    // The dialog ships with `open` so it still reads as a confirmation without
    // JS. With JS, close it and reopen as a real modal for focus trapping.
    if (typeof this.dialog.showModal !== 'function') return;

    this.dialog.removeAttribute('open');
    this.dialog.showModal();
    this.dialog.querySelector('.contact-success__title')?.focus();

    this.dialog.querySelectorAll('[data-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    // Clicking the backdrop lands on the dialog element itself.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    // Drop ?contact_posted=true now rather than on close: once the
    // confirmation has been shown, a refresh should not replay it.
    const url = new URL(window.location.href);
    if (url.searchParams.has('contact_posted')) {
      url.searchParams.delete('contact_posted');
      window.history.replaceState({}, '', url);
    }
  }

}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm);
}
